import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthUser } from '../types/user';
import { authApi } from '../api/client';
import { useRealtorStore } from '../plugins/realtor/store';

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  
  // Actions
  setUser: (user: AuthUser) => void;
  setToken: (token: string) => void;
  login: (user: AuthUser, token: string) => void;
  logout: () => void;
  updateUser: (updates: Partial<AuthUser>) => void;
  setLoading: (loading: boolean) => void;
  initAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isInitializing: true,

      setUser: (user) => set({ user, isAuthenticated: true }),
      
      setToken: (token) => set({ token }),
      
      login: (user, token) => set({ 
        user, 
        token, 
        isAuthenticated: true,
        isInitializing: false,
      }),
      
      logout: () => {
        console.log('Logout called');
        // Reset plugin stores
        useRealtorStore.getState().reset();
        set({ 
          user: null, 
          token: null, 
          isAuthenticated: false,
        });
      },
      
      updateUser: (updates) => set((state) => ({
        user: state.user ? { ...state.user, ...updates } : null,
      })),
      
      setLoading: (isInitializing) => set({ isInitializing }),

      initAuth: async () => {
        const state = get();
        
        console.log('initAuth called', {
          hasStoredToken: !!state.token,
          isAuthenticated: state.isAuthenticated,
          hasUser: !!state.user
        });

        // Check for Telegram WebApp
        const tg = (window as any).Telegram?.WebApp;
        if (tg?.initData) {
          console.log('In Telegram - authenticating via initData');
          
          set({ 
            user: null, 
            token: null, 
            isAuthenticated: false 
          });
          
          try {
            tg.expand?.();
            tg.ready?.();
            
            const res = await authApi.telegramAuth(tg.initData);
            const { token, user, group_ids, group_names, is_admin } = res.data.data;
            
            // Initialize realtor plugin from deep link
            const startParam = tg.initDataUnsafe?.start_param || '';
            if (startParam) {
              useRealtorStore.getState().initFromDeepLink(startParam);
            }
            
            set({
              token,
              user: {
                ...user,
                groupIds: group_ids || [],
                groupNames: group_names || [],
                permissions: [],
                isAdmin: is_admin || false,
              },
              isAuthenticated: true,
              isInitializing: false,
            });
            console.log('Telegram auth successful, user:', user.login);
            return;
          } catch (error: any) {
            console.error('Telegram auth failed:', error);
            set({ 
              user: null, 
              token: null, 
              isAuthenticated: false,
              isInitializing: false 
            });
            return;
          }
        }

        // Web client - check stored auth
        if (state.isAuthenticated && state.token && state.user) {
          console.log('Web client: validating stored token...');
          try {
            const res = await authApi.me();
            const { user, group_ids, group_names, is_admin } = res.data.data;
            
            set({
              user: {
                ...state.user!,
                ...user,
                groupIds: group_ids || state.user?.groupIds || [],
                groupNames: group_names || state.user?.groupNames || [],
                isAdmin: is_admin ?? state.user?.isAdmin ?? false,
              },
              isInitializing: false,
            });
            console.log('=== AUTH ME RESPONSE ===');
            console.log('is_admin from server:', is_admin);
            console.log('user.login:', user.login);
            console.log('Token valid, user:', user.login);
          } catch (error: any) {
            console.warn('Token validation failed:', error.response?.status);
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isInitializing: false,
            });
          }
        } else {
          console.log('No stored auth found');
          set({ isInitializing: false });
        }
      },
    }),
    {
      name: 'nre-auth',
      partialize: (state) => ({ 
        user: state.user, 
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        console.log('Auth state rehydrated', {
          hasUser: !!state?.user,
          hasToken: !!state?.token,
          isAuthenticated: state?.isAuthenticated
        });
      },
    }
  )
);
